import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    Typography,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import { CommunicationType } from "../../../data/CommunicationType";
import IconList from "../../../data/account/socialMediaIcons";
import ConfirmationModal from "../components/ConfirmationModal";

interface Props {
    communicationTypes: CommunicationType[];
    onEdit: (type: CommunicationType) => void;
    onDeleteClick: (type: CommunicationType) => Promise<void>;
    onViewDetails?: (type: CommunicationType) => void;
    deleteLoading?: boolean;
}

const getIconComponent = (iconValue: string) => {
    const IconComponent = IconList[iconValue];
    return IconComponent ? <IconComponent /> : "N/A";
};

const CommunicationTypesList: React.FC<Props> = ({
                                                     communicationTypes,
                                                     onEdit,
                                                     onDeleteClick,
                                                     onViewDetails,
                                                     deleteLoading,
                                                 }) => {
    const [isConfirmationOpen, setConfirmationOpen] = useState(false);
    const [selectedType, setSelectedType] = useState<CommunicationType | null>(null);

    const handleOpenConfirmation = (type: CommunicationType) => {
        setSelectedType(type);
        setConfirmationOpen(true);
    };

    const handleCloseConfirmation = () => {
        setSelectedType(null);
        setConfirmationOpen(false);
    };

    const handleConfirmDelete = async () => {
        if (!selectedType) return;
        try {
            await onDeleteClick(selectedType);
            handleCloseConfirmation();
        } catch (error) {
            console.error("Error deleting communication type:", error);
        }
    };

    return (
        <TableContainer sx={{ border: "1px solid #ddd", borderRadius: "8px" }}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell><b>Name</b></TableCell>
                        <TableCell><b>Icon</b></TableCell>
                        <TableCell><b>Tags</b></TableCell>
                        <TableCell><b>Actions</b></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {communicationTypes.map((type) => {
                        const maxRows = 2;
                        const tagsPerRow = 4;
                        const maxTagsToShow = maxRows * tagsPerRow;
                        const visibleTags = (type.tags || []).slice(0, maxTagsToShow);
                        const hiddenTagsCount = (type.tags?.length || 0) - visibleTags.length;

                        return (
                            <TableRow
                                key={type.comTypeId}
                                onClick={() => onViewDetails && onViewDetails(type)}
                                sx={{ cursor: onViewDetails ? "pointer" : "default" }}
                            >
                                <TableCell>{type.comType}</TableCell>
                                <TableCell>{getIconComponent(type.icon)}</TableCell>
                                <TableCell>
                                    <div style={{
                                        display: "grid",
                                        gridTemplateColumns: `repeat(${tagsPerRow}, auto)`,
                                        gridAutoFlow: "row",
                                        gap: "4px",
                                        maxHeight: `${maxRows * 36}px`,
                                        overflow: "hidden",
                                    }}>
                                        {visibleTags.map((tag) => (
                                            <Chip
                                                key={tag}
                                                label={tag}
                                                sx={{
                                                    backgroundColor: "#2196F3",
                                                    color: "white !important",
                                                    fontWeight: "bold",
                                                    borderRadius: "12px",
                                                    padding: "0px 8px",
                                                    fontSize: "0.75rem",
                                                    minWidth: "auto",
                                                    height: "28px",
                                                    "& .MuiChip-label": {
                                                        color: "white !important",
                                                        fontWeight: "bold",
                                                        padding: "0px 6px",
                                                    },
                                                }}
                                            />
                                        ))}
                                    </div>
                                    {hiddenTagsCount > 0 && (
                                        <Typography variant="caption" sx={{ marginLeft: 1 }}>
                                            +{hiddenTagsCount} more
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                    <IconButton onClick={() => onEdit(type)}>
                                        <Edit />
                                    </IconButton>
                                    <IconButton onClick={() => onDeleteClick(type)}>
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>

            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={isConfirmationOpen}
                onClose={handleCloseConfirmation}
                onConfirm={handleConfirmDelete} // ✅ Calls delete API
                confirmLoading={deleteLoading} // ✅ Pass loading state
                title="Delete Communication Type"
                message={`Are you sure you want to delete "${selectedType?.comType}"?`}
            />
        </TableContainer>
    );
};

export default CommunicationTypesList;